import type {
  AdminFamiliesQuery,
  AdminFamilyEvent,
  AdminFamilyState,
  AdminFamilySummary,
} from '@/admin/adminApi';
import { ABSENT, babyRoleLabel, pageCount, totalIsKnown, type PageState } from '@/admin/users/accountPresentation';
import type { BadgeTone } from '@/ui/kit';

/**
 * Mise en forme de « Familles et partages », ecrite **hors des composants** : elle
 * se teste sans rendu et ne peut pas diverger entre la liste et la fiche.
 *
 * Rien n'est calcule ici : les nombres (membres, invitations, age en mois) sont
 * ceux que le serveur rend, seulement mis en phrase.
 */

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

const DAY_FORMAT = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' });

/** Jour en toutes lettres, « — » quand la date manque ou est illisible. */
export const formatDay = (iso: string | null): string => {
  if (iso === null || iso === '') {
    return ABSENT;
  }

  const date = new Date(iso);

  return Number.isNaN(date.getTime()) ? ABSENT : DAY_FORMAT.format(date);
};

const plural = (count: number, one: string, many: string): string =>
  `${NUMBER_FORMAT.format(count)} ${count === 1 ? one : many}`;

/** L'age est dit en mois, tel que le serveur le rend (jamais converti en annees). */
export const ageLabel = (months: number): string => `${NUMBER_FORMAT.format(months)} mois`;

/** « 8 mois · 2 membres · 1 invitation en attente » ; la partie invitation manque a zero. */
export const familySubtitle = (family: AdminFamilySummary): string =>
  [
    ageLabel(family.ageMonths),
    plural(family.memberCount, 'membre', 'membres'),
    ...(family.pendingInvitationCount > 0
      ? [plural(family.pendingInvitationCount, 'invitation en attente', 'invitations en attente')]
      : []),
  ].join(' · ');

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

const INVITATION_STATUS: Readonly<Record<string, { readonly label: string; readonly tone: BadgeTone }>> = {
  pending: { label: 'En attente', tone: 'warning' },
  accepted: { label: 'Acceptée', tone: 'success' },
  declined: { label: 'Refusée', tone: 'neutral' },
  revoked: { label: 'Révoquée', tone: 'neutral' },
  expired: { label: 'Expirée', tone: 'neutral' },
};

/** Un statut inconnu est affiche tel quel, jamais masque derriere un mot generique. */
export const invitationStatus = (status: string): { readonly label: string; readonly tone: BadgeTone } =>
  INVITATION_STATUS[status] ?? { label: status, tone: 'neutral' };

// ---------------------------------------------------------------------------
// Historique du partage
// ---------------------------------------------------------------------------

/** Un compte anonymise n'a plus d'adresse : on le dit, on ne l'invente pas. */
export const ANONYMIZED_ACCOUNT = 'Compte anonymisé';

const accountLabel = (email: string | null): string => email ?? ANONYMIZED_ACCOUNT;

/** La cible d'un evenement : son adresse, « Compte anonymisé » si elle manque. */
const targetLabel = (event: AdminFamilyEvent): string =>
  event.targetUserId === null ? ABSENT : accountLabel(event.targetEmail);

const roleAfter = (event: AdminFamilyEvent): string =>
  event.role === null ? '' : ` (${babyRoleLabel(event.role).toLowerCase()})`;

/**
 * L'evenement en une phrase. Une action inconnue est rendue **telle que
 * stockee** : la contrainte du serveur gagne des valeurs avec le temps.
 */
export const describeFamilyEvent = (event: AdminFamilyEvent): string => {
  switch (event.action) {
    case 'invitation_created':
      return `Invitation créée${roleAfter(event)}`;
    case 'invitation_accepted':
      return 'Invitation acceptée';
    case 'invitation_declined':
      return 'Invitation refusée';
    case 'invitation_revoked':
      return 'Invitation révoquée';
    case 'member_role_changed':
      return `Rôle de ${targetLabel(event)} changé${roleAfter(event)}`;
    case 'member_revoked':
      return `Accès de ${targetLabel(event)} retiré`;
    case 'member_left':
      return 'A quitté le foyer';
    case 'ownership_transferred':
      return `Propriété transférée à ${targetLabel(event)}`;
    case 'baby_registered_from_sync':
      return 'Profil enregistré sur le serveur par la synchronisation';
    case 'baby_registered_by_backfill':
      return 'Profil repris du journal de synchronisation';
    default:
      return event.action;
  }
};

export const eventActorLabel = (event: AdminFamilyEvent): string => accountLabel(event.actorEmail);

// ---------------------------------------------------------------------------
// Filtres et pagination
// ---------------------------------------------------------------------------

export type FamilyFilters = AdminFamiliesQuery;

export const DEFAULT_FAMILY_FILTERS: FamilyFilters = { search: '', state: null, pendingInvitation: false };

/**
 * Valeur de l'option « tous » : une option a valeur vide ne s'afficherait pas
 * une fois choisie (MUI laisse le champ blanc). L'URL et la requete, elles,
 * n'ont aucune valeur d'etat pour « tous ».
 */
export const ANY_STATE = 'all';

export const FAMILY_STATE_OPTIONS: readonly { readonly value: string; readonly label: string }[] = [
  { value: ANY_STATE, label: 'Tous les profils' },
  { value: 'active', label: 'Actifs' },
  { value: 'archived', label: 'Archivés' },
];

const isState = (value: string | null): value is AdminFamilyState => value === 'active' || value === 'archived';

export const hasActiveFamilyFilter = (filters: FamilyFilters): boolean =>
  filters.search !== '' || filters.state !== null || filters.pendingInvitation;

/** Lit les filtres de l'URL ; une valeur illisible retombe sur « pas de filtre ». */
export const readFamilyFilters = (params: URLSearchParams): FamilyFilters => {
  const state = params.get('state');

  return {
    search: (params.get('q') ?? '').trim(),
    state: isState(state) ? state : null,
    pendingInvitation: params.get('pending') === '1',
  };
};

export const readFamilyPage = (params: URLSearchParams): number => {
  const raw = params.get('page') ?? '';

  return /^[1-9][0-9]{0,5}$/.test(raw) ? Number(raw) : 1;
};

export const writeFamilyFilters = (filters: FamilyFilters, page = 1): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.search !== '') {
    params.set('q', filters.search);
  }

  if (filters.state !== null) {
    params.set('state', filters.state);
  }

  if (filters.pendingInvitation) {
    params.set('pending', '1');
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  return params;
};

/**
 * Phrase de pagination. Quand le serveur n'a pas rendu de total, elle le dit au
 * lieu d'en inventer un.
 */
export const describeFamiliesPage = (state: PageState): string => {
  if (!totalIsKnown(state.total)) {
    return `Page ${NUMBER_FORMAT.format(state.page)} · ${plural(state.itemCount, 'profil affiché', 'profils affichés')} · nombre total non communiqué par le serveur`;
  }

  const total = state.total === 0 ? 'Aucun profil' : plural(state.total, 'profil', 'profils');

  return `${total} · page ${NUMBER_FORMAT.format(state.page)} sur ${NUMBER_FORMAT.format(pageCount(state))}`;
};
